import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, normalizeClass as _normalizeClass, withModifiers as _withModifiers, createElementVNode as _createElementVNode } from "vue"

import {
        Dialog,
        DialogBody,
        DialogContent,
        DialogHeader,
        DialogTitle,
        DialogTrigger,
    } from '@/components/ui/dialog';
    import { toast } from 'vue-sonner';
    import { useI18n } from 'vue-i18n';
    import { ref, computed, watch } from 'vue';
    import EditButton from '@/components/EditButton.vue';
    import DialogFooterForm from '@/components/DialogFooterForm.vue';
    import { useQueryClient } from '@tanstack/vue-query';
    import CreateButton from '@/components/CreateButton.vue';
    import { ButtonVariants } from '@/components/ui/button';

    
export default /*@__PURE__*/_defineComponent({
  __name: 'DialogForm',
  props: {
    type: {},
    form: {},
    route: {},
    queryKey: {},
    title: {},
    successMessage: { default: 'forms.general.success' },
    successMessageTranslated: {},
    errorMessage: { default: 'forms.general.error' },
    method: {},
    modalSize: { default: 'sm:max-w-md' },
    buttonText: {},
    submitText: {},
    footerClass: {},
    transform: {},
    disabled: { type: Boolean },
    submitVariant: {},
    hideSuccessToast: { type: Boolean, default: false },
    hideErrorToast: { type: Boolean, default: false },
    hideFooter: { type: Boolean, default: false },
    open: { type: Boolean },
    hideTrigger: { type: Boolean, default: false },
    afterSuccess: {}
  },
  emits: ['success', 'error', 'open', 'update:open', 'finish'],
  setup(__props: any, { expose: __expose, emit: __emit }) {

    const queryClient = useQueryClient();

    const emit = __emit;

    const { t } = useI18n();

    const isOpen = ref(false);

    const props = __props;

    const isControlled = computed(() => props.open !== undefined);

    watch(
        () => props.open,
        (open) => {
            if (open !== undefined) {
                isOpen.value = open;
            }
        },
        { immediate: true },
    );

    const methodType = computed(() => {
        if (props.method) {
            return props.method;
        }
        return props.type === 'create' ? 'post' : 'put';
    });

    const action = () => {
        const formToSubmit = props.transform ? props.form.transform(props.transform) : props.form;

        // Capture at submit time so the callback survives an Inertia page
        // remount (e.g. when the endpoint responds with back()).
        const afterSuccess = props.afterSuccess;

        formToSubmit[methodType.value](props.route, {
            preserveScroll: true,
            onSuccess: () => {
                if (props.type === 'create') {
                    props.form.resetAndClearErrors();
                } else {
                    props.form.clearErrors();
                }
                emit('success');

                invalidateQueryAndClose();

                if (!props.hideSuccessToast) {
                    toast.success(props.successMessageTranslated || t(props.successMessage));
                }

                afterSuccess?.();
            },
            onFinish: () => {
                emit('finish');
            },
            onError: (error: any) => {
                if (!props.hideErrorToast) {
                    toast.error(error?.error || t(props.errorMessage));
                }

                emit('error', error);
            },
        });
    };

    const invalidateQueryAndClose = () => {
        queryClient.invalidateQueries({ queryKey: props.queryKey });
        isOpen.value = false;
    };

    watch(isOpen, (open) => {
        if (isControlled.value) {
            emit('update:open', open);
        }

        if (open) {
            emit('open');
            props.form.resetAndClearErrors();
        }
    });

    __expose({
        invalidateQueryAndClose,
    });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Dialog), {
    open: isOpen.value,
    "onUpdate:open": _cache[1] || (_cache[1] = ($event: any) => ((isOpen).value = $event))
  }, {
    default: _withCtx(() => [
      (!_ctx.hideTrigger)
        ? (_openBlock(), _createBlock(_unref(DialogTrigger), {
            key: 0,
            "as-child": ""
          }, {
            default: _withCtx(() => [
              _renderSlot(_ctx.$slots, "trigger", {}, () => [
                (_ctx.type === 'update')
                  ? (_openBlock(), _createBlock(EditButton, {
                      key: 0,
                      disabled: _ctx.disabled
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)(_ctx.buttonText || 'tables.edit')), 1)
                      ]),
                      _: 1
                    }, 8, ["disabled"]))
                  : _createCommentVNode("", true),
                (_ctx.type === 'create')
                  ? (_openBlock(), _createBlock(CreateButton, {
                      key: 1,
                      "button-text": _ctx.buttonText,
                      disabled: _ctx.disabled
                    }, null, 8, ["button-text", "disabled"]))
                  : _createCommentVNode("", true)
              ])
            ]),
            _: 3
          }))
        : _createCommentVNode("", true),
      _createVNode(_unref(DialogContent), {
        class: _normalizeClass(_ctx.modalSize)
      }, {
        default: _withCtx(() => [
          _createElementVNode("form", {
            onSubmit: _withModifiers(action, ["prevent"]),
            class: "flex max-h-full flex-col overflow-hidden"
          }, [
            _createVNode(_unref(DialogHeader), null, {
              default: _withCtx(() => [
                _createVNode(_unref(DialogTitle), null, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)(_ctx.title)), 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_unref(DialogBody), { class: "space-y-5" }, {
              default: _withCtx(() => [
                _renderSlot(_ctx.$slots, "default")
              ]),
              _: 3
            }),
            _renderSlot(_ctx.$slots, "footer", {}, () => [
              _createVNode(DialogFooterForm, {
                form: _ctx.form,
                type: _ctx.type,
                onClose: _cache[0] || (_cache[0] = ($event: any) => (_ctx.form.resetAndClearErrors())),
                class: _normalizeClass(_ctx.footerClass),
                "submit-text": _ctx.submitText,
                "submit-variant": _ctx.submitVariant
              }, {
                default: _withCtx(() => [
                  _renderSlot(_ctx.$slots, "footerActions")
                ]),
                _: 3
              }, 8, ["form", "type", "class", "submit-text", "submit-variant"])
            ])
          ], 32)
        ]),
        _: 3
      }, 8, ["class"])
    ]),
    _: 3
  }, 8, ["open"]))
}
}

})